import React from 'react';
import { InputNumber, Select } from '@bedrock/components';
import { SelectProps } from '@bedrock/components/es/Select';
import { useControllableValue } from 'ahooks';
import classNames from 'classnames/bind';
import styles from './index.less';
import { currencyOptions, currencyEnum, financial } from '#utils';

const cx = classNames.bind(styles);

export interface NumberPriceRangeValue {
  min?: number;
  max?: number;
  currency?: string;
  [key: string]: any; // 支持自定义字段
}

export interface NumberPriceRangeProps {
  value?: NumberPriceRangeValue;
  onChange?: (value: NumberPriceRangeValue) => void;
  showCurrency?: boolean;
  selectProps?: SelectProps;
  minProps?: Omit<
    React.ComponentProps<typeof InputNumber>,
    'value' | 'onChange'
  >;
  maxProps?: Omit<
    React.ComponentProps<typeof InputNumber>,
    'value' | 'onChange'
  >;
  view?: boolean;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** 自定义字段名，支持传入自定义的 min、max、currency 字段名 */
  fieldNames?: {
    min?: string;
    max?: string;
    currency?: string;
  };
  /** 外部传入的 currency 值，传入时优先使用此值，不使用 value 中的 currency */
  currency?: string;
  /** 是否允许下限为空（单边价格场景，#9510）：true 时仅上限必填，展示态下限空显示 0；默认 false 保持存量区间价格行为 */
  allowEmptyMin?: boolean;
}

interface NumberPriceRangeRef {
  validateFields: () => Promise<boolean>;
}

const emptyValue = (value: number | null | undefined | string) => {
  return value === undefined || value === null || value === '';
};

// 验证函数：检查必填项是否全部填写
const validateRequired = (
  value: NumberPriceRangeValue,
  showCurrency: boolean = true,
  fieldNames: { min: string; max: string; currency: string } = {
    min: 'min',
    max: 'max',
    currency: 'currency',
  },
  hasCurrencyProp: boolean = false,
  allowEmptyMin: boolean = false
): boolean => {
  // allowEmptyMin=true（单边价格场景）：仅上限必填，下限可空
  if (allowEmptyMin) {
    if (emptyValue(value?.[fieldNames.max])) {
      return false;
    }
  } else {
    if (
      emptyValue(value?.[fieldNames.min]) ||
      emptyValue(value?.[fieldNames.max])
    ) {
      return false;
    }
  }
  // 如果传入了 currency prop，则不需要验证 value 中的 currency 字段
  if (
    showCurrency &&
    !hasCurrencyProp &&
    emptyValue(value?.[fieldNames.currency])
  ) {
    return false;
  }
  return true;
};

const NumberPriceRange = React.forwardRef<
  NumberPriceRangeRef,
  NumberPriceRangeProps
>((props, ref) => {
  const {
    value,
    onChange,
    showCurrency = true,
    selectProps,
    minProps,
    maxProps,
    view = false,
    required = false,
    className,
    style,
    fieldNames,
    currency: currencyProp,
    allowEmptyMin = false,
  } = props;

  // 使用自定义字段名或默认字段名
  const fieldNamesConfig = {
    min: fieldNames?.min || 'min',
    max: fieldNames?.max || 'max',
    currency: fieldNames?.currency || 'currency',
  };

  // 根据字段名构建默认值
  const getDefaultValue = () => {
    const defaultValue: NumberPriceRangeValue = {};
    defaultValue[fieldNamesConfig.min] = undefined;
    defaultValue[fieldNamesConfig.max] = undefined;
    defaultValue[fieldNamesConfig.currency] = undefined;
    return defaultValue;
  };

  const [state, setState] = useControllableValue<NumberPriceRangeValue>(props, {
    defaultValue: getDefaultValue(),
  });

  // 暴露验证方法给父组件
  React.useImperativeHandle(ref, () => ({
    validateFields: async () => {
      if (!state && required) {
        return Promise.reject(false);
      }
      const isValid = validateRequired(
        state,
        showCurrency,
        fieldNamesConfig,
        currencyProp !== undefined,
        allowEmptyMin
      );
      if (!isValid && required) {
        return Promise.reject('此项必填');
      }
      // 校验 min 不能大于 max
      const minValue = state?.[fieldNamesConfig.min];
      const maxValue = state?.[fieldNamesConfig.max];
      if (!emptyValue(minValue) && !emptyValue(maxValue)) {
        const minNum =
          typeof minValue === 'number' ? minValue : Number(minValue);
        const maxNum =
          typeof maxValue === 'number' ? maxValue : Number(maxValue);
        if (!isNaN(minNum) && !isNaN(maxNum) && minNum > maxNum) {
          return Promise.reject('下限不能大于上限');
        }
      }
      return Promise.resolve(true);
    },
  }));

  const handleMinChange = (val: number | null | undefined) => {
    const newValue = {
      ...state,
      [fieldNamesConfig.min]: val ?? undefined,
    };
    setState(newValue);
    onChange?.(newValue);
  };

  const handleMaxChange = (val: number | null | undefined) => {
    const newValue = {
      ...state,
      [fieldNamesConfig.max]: val ?? undefined,
    };
    setState(newValue);
    onChange?.(newValue);
  };

  const handleCurrencyChange = (val: string) => {
    // 如果传入了 currency prop，则不允许通过 Select 修改 currency
    if (currencyProp !== undefined) {
      return;
    }
    const newValue = {
      ...state,
      [fieldNamesConfig.currency]: val,
    };
    setState(newValue);
    onChange?.(newValue);
  };

  // 展示态
  if (view) {
    const minValue = state?.[fieldNamesConfig.min];
    const maxValue = state?.[fieldNamesConfig.max];
    // 优先使用外部传入的 currency prop，否则使用 value 中的 currency
    const currencyValue =
      currencyProp !== undefined
        ? currencyProp
        : state?.[fieldNamesConfig.currency];

    if (emptyValue(minValue) && emptyValue(maxValue)) {
      return '--';
    }
    return (
      <div
        className={cx('number-range', 'number-range-view', className)}
        style={style}
      >
        <div className={cx('number-range-inputs', 'number-range-inputs-view')}>
          <div className={cx('number-range-view-value')}>
            {!emptyValue(minValue)
              ? financial(minValue)
              : allowEmptyMin
                ? '0'
                : '--'}
          </div>
          <span className={cx('number-range-separator')}>-</span>
          <div className={cx('number-range-view-value')}>
            {!emptyValue(maxValue) ? financial(maxValue) : '--'}
          </div>
        </div>
        {showCurrency && (
          <div className={cx('number-range-currency')}>
            <div className={cx('number-range-view-currency')}>
              {currencyValue
                ? currencyEnum[currencyValue.toLowerCase()] || currencyValue
                : '--'}
            </div>
          </div>
        )}
      </div>
    );
  }

  // 编辑态
  const minValue = state?.[fieldNamesConfig.min];
  const maxValue = state?.[fieldNamesConfig.max];
  // 优先使用外部传入的 currency prop，否则使用 value 中的 currency
  const currencyValue =
    currencyProp !== undefined
      ? currencyProp
      : state?.[fieldNamesConfig.currency];
  // 如果传入了 currency prop，则禁用 Select（因为 currency 由外部控制）
  const isCurrencyDisabled = currencyProp !== undefined;

  return (
    <div className={cx('number-range', className)} style={style}>
      <div className={cx('number-range-inputs')}>
        <InputNumber
          className={cx('number-range-min')}
          placeholder={allowEmptyMin ? '0' : '价格下限'}
          value={minValue}
          onChange={handleMinChange}
          direction="vertical"
          allowClear
          toFixed={2}
          max={Number.MAX_SAFE_INTEGER - 1}
          formatter={(value) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          }
          {...minProps}
        />
        <span className={cx('number-range-separator')}>-</span>
        <InputNumber
          className={cx('number-range-max')}
          placeholder="价格上限"
          value={maxValue}
          onChange={handleMaxChange}
          direction="vertical"
          allowClear
          toFixed={2}
          max={Number.MAX_SAFE_INTEGER - 1}
          formatter={(value) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          }
          {...maxProps}
        />
      </div>
      {showCurrency && (
        <div className={cx('number-range-currency')}>
          <Select
            className={cx('number-range-select')}
            value={currencyValue?.toLowerCase?.()}
            onChange={handleCurrencyChange}
            placeholder="请选择"
            options={currencyOptions}
            disabled={isCurrencyDisabled}
            {...selectProps}
          />
        </div>
      )}
    </div>
  );
});

export default NumberPriceRange;
